import React from "react";
import { cn } from "@/lib/utils";
import { 
  type MusicianPosition, 
  POSITION_LABELS,
  type InstrumentWithPosition 
} from "@/lib/techRiderData";

interface StageDiagramProps {
  instruments: InstrumentWithPosition[];
}

// Position coordinates on stage (percentage based)
const getPositionCoords = (index: number, total: number, position: MusicianPosition) => {
  // Distribute instruments across the stage
  const row = Math.floor(index / 4);
  const col = index % 4;
  const itemsInRow = Math.min(4, total - row * 4);
  
  // Center the row
  const startX = 50 - (itemsInRow - 1) * 12;
  let x = startX + col * 24;
  
  // Y position based on musician position type
  let y: number;
  switch (position) {
    case 'floor':
      y = 75 + row * 15; // Front, floor level
      break;
    case 'seated-chair':
    case 'stool':
      y = 55 + row * 15; // Middle
      break;
    case 'mobile':
      y = 35 + row * 15; // Can move around
      break;
    case 'table-stand':
      y = 45 + row * 15; // DJ/Percussion area
      break;
    default:
      y = 50 + row * 15; // Standing - center
  }
  
  return { x: Math.max(10, Math.min(90, x)), y: Math.max(15, Math.min(85, y)) };
};

const getPositionIcon = (position: MusicianPosition): string => {
  switch (position) {
    case 'standing': return '🧍';
    case 'sitting': return '🪑';
    case 'seated-chair': return '💺';
    case 'stool': return '🔘';
    case 'floor': return '🧘';
    case 'mobile': return '🚶';
    case 'table-stand': return '🎛️';
    default: return '🎵';
  }
};

const getInstrumentAbbr = (name: string): string => {
  // Create short abbreviation for instrument
  const abbrs: Record<string, string> = {
    'Violinists': 'VLN',
    'Cellists': 'VLC',
    'Harpists': 'HRP',
    'Flautists': 'FLT',
    'Trumpeters': 'TPT',
    'Trombonists': 'TBN',
    'Saxophonists': 'SAX',
    'Pianists': 'PNO',
    'Drummers': 'DRM',
    'Tabla Players': 'TBL',
    'Dhol Players': 'DHL',
    'Bansuri Players': 'BAN',
    'Shehnai Players': 'SHN',
    'Sitar Players': 'STR',
    'Vocalist': 'VOX',
    'DJ': 'DJ',
    'Acoustic Guitarists': 'GTR',
    'Electric Guitarists': 'EGT',
    'Bass Guitarists': 'BSS',
    'Keyboard Players': 'KEY',
    'Handpan Players': 'HPN',
    'Dilruba Players': 'DLR',
    'Tumbi Players': 'TMB',
    'Percussionists': 'PRC',
  };
  return abbrs[name] || name.substring(0, 3).toUpperCase();
};

export function StageDiagram({ instruments }: StageDiagramProps) {
  if (instruments.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="font-semibold text-sm">Stage Plot</h4>
      
      {/* Stage diagram */}
      <div className="relative w-full aspect-[16/10] bg-gradient-to-b from-muted/30 to-muted/60 rounded-lg border-2 border-border overflow-hidden">
        {/* Stage front label */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-primary/20 flex items-center justify-center">
          <span className="text-[10px] font-medium text-primary">AUDIENCE / FRONT OF STAGE</span>
        </div>
        
        {/* Back of stage label */}
        <div className="absolute top-1 left-0 right-0 flex justify-center">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Back of Stage</span>
        </div>
        
        {/* AV/DI position marker */}
        <div className="absolute top-3 right-2">
          <div className="px-1.5 py-0.5 bg-amber-500/20 rounded text-[9px] text-amber-700 font-medium border border-amber-500/30">
            DI / LAPTOP
          </div>
        </div>
        
        {/* Instrument positions */}
        {instruments.map((inst, i) => {
          const coords = getPositionCoords(i, instruments.length, inst.position);
          return (
            <div
              key={i}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5"
              style={{ left: `${coords.x}%`, top: `${coords.y}%` }}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 shadow-sm",
                inst.position === 'floor' && "bg-accent/50 border-accent",
                inst.position === 'seated-chair' && "bg-primary/20 border-primary",
                inst.position === 'stool' && "bg-secondary border-secondary-foreground/50",
                inst.position === 'standing' && "bg-muted border-muted-foreground",
                inst.position === 'mobile' && "bg-destructive/20 border-destructive animate-pulse",
                inst.position === 'table-stand' && "bg-card border-border",
              )}>
                {getPositionIcon(inst.position)}
              </div>
              <span className="text-[9px] font-bold bg-background/90 px-1 rounded shadow-sm whitespace-nowrap">
                {getInstrumentAbbr(inst.name)}
              </span>
            </div>
          );
        })}
        
        {/* Legend */}
        <div className="absolute bottom-7 left-1 flex flex-wrap gap-1">
          {Object.entries(POSITION_LABELS)
            .filter(([key]) => instruments.some(i => i.position === key))
            .map(([key, label]) => (
              <div key={key} className="flex items-center gap-0.5 text-[8px] bg-background/80 px-1 py-0.5 rounded">
                <span>{getPositionIcon(key as MusicianPosition)}</span>
                <span className="text-muted-foreground">{label}</span>
              </div>
            ))}
        </div>
      </div>
      
      {/* Position summary */}
      <div className="text-xs text-muted-foreground">
        <span className="font-medium">Setup:</span>{' '}
        {instruments.map((inst, i) => (
          <span key={i}>
            {inst.name} ({POSITION_LABELS[inst.position]})
            {i < instruments.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}